import type { StatusTagItem } from '@/components';
import { ProductionOrderStatus } from '@/types';

/** How one status is drawn, before it is paired with the wire value it renders. */
type StatusPresentation = Omit<StatusTagItem, 'value' | 'label'> & {
  label: string;
};

/**
 * Presentation for every status this frontend mirrors. Keyed by the enum on
 * purpose: a new member fails to compile until it declares a label and colour,
 * so a status the frontend knows can never fall through to the unrecognised
 * tag below — that fallback is reserved for values only the backend has.
 */
const PRODUCTION_ORDER_STATUS_PRESENTATION: Record<
  ProductionOrderStatus,
  StatusPresentation
> = {
  [ProductionOrderStatus.DRAFT]: { label: 'Draft', color: 'neutral' },
  [ProductionOrderStatus.TO_EXPORT]: { label: 'To Export', color: 'yellow' },
  [ProductionOrderStatus.EXPORTED]: { label: 'Exported', color: 'green' },
  [ProductionOrderStatus.CREATED]: { label: 'Created', color: 'cyan' },
  [ProductionOrderStatus.RELEASED]: { label: 'Released', color: 'blue' },
  [ProductionOrderStatus.CLOSED]: {
    label: 'Closed',
    color: 'neutral-darker'
  },
  [ProductionOrderStatus.TO_UPDATE]: {
    label: 'To Update',
    color: 'orange-darker'
  },
  [ProductionOrderStatus.TECO]: { label: 'TeCo', color: 'purple' },
  [ProductionOrderStatus.CANCELLED]: {
    label: 'Cancelled',
    color: 'neutral-darker'
  },
  [ProductionOrderStatus.TO_CANCEL]: {
    label: 'To Cancel',
    color: 'red-inverse'
  },
  [ProductionOrderStatus.DIVERGENT]: {
    label: 'Divergent',
    color: 'neutral-darkest'
  }
};

const PRESENTATION_ENTRIES = Object.entries(
  PRODUCTION_ORDER_STATUS_PRESENTATION
) as Array<[ProductionOrderStatus, StatusPresentation]>;

/**
 * `StatusTag` items for a Production Order's Status column. Single source of
 * the human-readable label, so copy that has to name statuses — the
 * non-editable notice, for one — never drifts from the tag the user sees.
 */
export const PRODUCTION_ORDER_STATUS_ITEMS: StatusTagItem[] =
  PRESENTATION_ENTRIES.map(([value, presentation]) => ({
    value,
    ...presentation
  }));

export const PRODUCTION_ORDER_STATUS_LABELS = Object.fromEntries(
  PRESENTATION_ENTRIES.map(([value, { label }]) => [value, label])
) as Record<ProductionOrderStatus, string>;

/**
 * Single-letter variant for status columns too narrow for the word. Several
 * statuses share a first letter; the colour and the full-name tooltip are what
 * tell them apart.
 */
const abbreviate = ({
  label,
  color
}: StatusPresentation): StatusPresentation => ({
  label: label.slice(0, 1),
  tooltip: label,
  color
});

export const ABBREVIATED_PRODUCTION_ORDER_STATUS_ITEMS: StatusTagItem[] =
  PRESENTATION_ENTRIES.map(([value, presentation]) => ({
    value,
    ...abbreviate(presentation)
  }));

/**
 * Reserved for a status this frontend does not know. It shares `DIVERGENT`'s
 * colour on purpose: both mean the real status cannot be trusted, and the raw
 * value in the label is what tells them apart.
 */
const UNKNOWN_PRODUCTION_ORDER_STATUS_COLOR = 'neutral-darkest' as const;

const KNOWN_PRODUCTION_ORDER_STATUSES = new Set<string>(
  Object.keys(PRODUCTION_ORDER_STATUS_PRESENTATION)
);

/**
 * Presentation for a wire status the items above do not carry, and `undefined`
 * for one they do — so a caller can pass the result straight through as a
 * fallback and learn from it whether the status was recognised at all. An empty
 * status is an absent one rather than an unrecognised one and gets no tag.
 *
 * The raw value is the label so an unmapped status is visible and debuggable
 * instead of a blank cell, and the tooltip carries it in full for the
 * abbreviated column, whose width only fits a letter.
 */
export const getUnknownProductionOrderStatusItem = (
  status: string,
  isAbbreviated = false
): StatusPresentation | undefined => {
  if (!status || KNOWN_PRODUCTION_ORDER_STATUSES.has(status)) {
    return undefined;
  }

  const presentation: StatusPresentation = {
    label: status,
    tooltip: status,
    color: UNKNOWN_PRODUCTION_ORDER_STATUS_COLOR
  };

  return isAbbreviated ? abbreviate(presentation) : presentation;
};
